import { Injectable, inject } from '@angular/core';
import * as L from 'leaflet';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { StorageService } from '../../services/storage/storage.service';
import { DrawStyleModalComponent } from '../controls/draw-style/draw-style-modal.component';
import { MapMarkerHandler, MAP_MARKER_HANDLER, MapMarker } from '../handlers';

/**
 * Bridge interface for GpkgRenderingService to access MapComponent functionality.
 */
export interface GpkgRenderingBridge {
  getPoints(): MapMarker[] | undefined;
  getPointsLayer(): L.Layer | undefined;
  isPointsLayerVisible(): boolean;
  getPointLabelMinZoom(): number;
}

/**
 * Service für GPKG Canvas-Label-Rendering, Haken-Marker und Geometrie-Utilities.
 * Extrahiert aus MapComponent (Plan 15 Phase 3a).
 */
@Injectable({ providedIn: 'root' })
export class GpkgRenderingService {
  private map!: L.Map;
  private bridge!: GpkgRenderingBridge;

  // GPKG Canvas-Labels
  gpkgLabelLayers = new Map<string, L.Layer>();
  private gpkgZoomHandlers = new Map<string, () => void>();
  readonly GPKG_LABEL_MIN_ZOOM = 15;
  // Globale Label-Rechtecke für Kollisionsprüfung (wird bei jedem Render geleert und neu befüllt)
  gpkgLabelRects: {x: number, y: number, w: number, h: number, priority: number}[] = [];
  // Aktuell versteckte GPKG-Layer (für Sichtbarkeits-Toggle)
  hiddenGpkgLayers = new Set<string>();
  // GPKG Haken-Layer (Punkt-Marker mit Häkchen-Symbol)
  gpkgHakenLayers = new Map<string, L.LayerGroup>();
  // Punkt-Label-Rechtecke für Kollisionsprüfung mit GPKG-Labels
  pointLabelRects: {x: number, y: number, w: number, h: number}[] = [];

  private storage = inject(StorageService);
  private modalService = inject(NgbModal);
  private markerHandler = inject(MAP_MARKER_HANDLER, { optional: true }) as MapMarkerHandler<unknown> | null;

  init(map: L.Map, bridge: GpkgRenderingBridge): void {
    this.map = map;
    this.bridge = bridge;
  }

  destroy(): void {
    // Cleanup zoom handlers
    this.gpkgZoomHandlers.forEach((handler) => {
      if (this.map) this.map.off('zoomend', handler);
    });
    this.gpkgZoomHandlers.clear();
    // Cleanup label layers
    this.gpkgLabelLayers.forEach((layer) => {
      if (this.map?.hasLayer(layer)) layer.removeFrom(this.map);
    });
    this.gpkgLabelLayers.clear();
    // Cleanup haken layers
    this.gpkgHakenLayers.forEach((layer) => {
      const zoomHandler = (layer as any)._hakenZoomHandler;
      if (zoomHandler && this.map) this.map.off('zoomend', zoomHandler);
      if (this.map?.hasLayer(layer)) layer.removeFrom(this.map);
    });
    this.gpkgHakenLayers.clear();
    this.gpkgLabelRects.length = 0;
    this.hiddenGpkgLayers.clear();
    this.pointLabelRects.length = 0;
  }

  /**
   * Räumt alle GPKG-Layer auf (vor Layer-Neuerstellung).
   */
  cleanupAllGpkgLayers(): void {
    // Alte GPKG-Label-Layer und Feature-Daten entfernen
    this.gpkgLabelLayers.forEach((layer) => {
      if (this.map.hasLayer(layer)) {
        layer.removeFrom(this.map);
      }
    });
    this.gpkgLabelLayers.clear();
    this.gpkgZoomHandlers.clear();

    // Alte GPKG-Haken-Layer entfernen (inkl. Zoom-Handler)
    this.gpkgHakenLayers.forEach((layer) => {
      const zoomHandler = (layer as any)._hakenZoomHandler;
      if (zoomHandler) {
        this.map.off('zoomend', zoomHandler);
      }
      if (this.map.hasLayer(layer)) {
        layer.removeFrom(this.map);
      }
    });
    this.gpkgHakenLayers.clear();
  }

  /**
   * Erkennt den GPKG-Style basierend auf Feature-Attributen.
   * Standort: Hat LBFO1, STGR1 Attribute
   * Humusform: Hat HUFO1 Attribut
   */
  detectGpkgStyleFromFeature(feature: any): 'standort' | 'humusform' | 'haken' | null {
    const props = feature?.properties || {};
    const keys = Object.keys(props).map(k => k.toUpperCase());

    // Standort: LBFO1, STGR1, SUBF1, SUB1
    if (keys.includes('LBFO1') || keys.includes('STGR1')) {
      return 'standort';
    }

    // Humusform: HUFO1
    if (keys.includes('HUFO1')) {
      return 'humusform';
    }

    // Haken: SYMBOL, ART, ANGLE
    if (keys.includes('SYMBOL') && keys.includes('ART') && keys.includes('ANGLE')) {
      return 'haken';
    }

    return null;
  }

  /**
   * Erstellt einen Layer mit Haken-Markern für GPKG Punkt-Features.
   * Haken werden als halbe Überhaken (wie ein Häkchen) dargestellt.
   * ART=humus -> grün, ART=sto -> rot, ANGLE gibt die Rotation an.
   */
  createHakenMarkerLayer(features: any[], layerName: string): L.LayerGroup | null {
    const markerData: {latlng: L.LatLng, color: string, angle: number}[] = [];

    for (const feature of features) {
      const geom = feature.geometry;
      if (!geom || geom.type !== 'Point') continue;

      const props = feature.properties || {};
      // Case-insensitive Attributzugriff
      const getProp = (key: string) => {
        const found = Object.keys(props).find(k => k.toUpperCase() === key.toUpperCase());
        return found ? props[found] : undefined;
      };

      const art = (getProp('ART') || '').toString().toLowerCase();
      const angle = parseFloat(getProp('ANGLE') || '0') || 0;

      // Farbe basierend auf ART
      let color: string;
      if (art === 'humus' || art.includes('humus')) {
        color = '#228B22'; // Grün
      } else if (art === 'sto' || art.includes('sto')) {
        color = '#cc0000'; // Rot
      } else {
        color = '#666666'; // Grau für unbekannte
      }

      // Koordinaten (GeoJSON: [lng, lat])
      const coords = geom.coordinates;
      if (!coords || coords.length < 2) continue;
      const latlng = L.latLng(coords[1], coords[0]);

      markerData.push({ latlng, color, angle });
    }

    if (markerData.length === 0) return null;

    const layerGroup = L.layerGroup();

    // Funktion zum Erstellen/Aktualisieren der Marker basierend auf Zoom
    const createMarkersForZoom = (zoom: number) => {
      layerGroup.clearLayers();

      // Unter Zoom 14: keine Anzeige
      if (zoom < 14) return;

      // Größe skaliert mit Zoom (Referenz: 24px bei Zoom 18)
      // Sanftere Skalierung: sqrt(2) statt 2 pro Zoomstufe
      const baseSize = 24;
      const scaleFactor = Math.pow(Math.SQRT2, zoom - 18);
      const size = Math.max(8, Math.round(baseSize * scaleFactor));
      const strokeWidth = Math.max(1, 2.5 * scaleFactor);

      for (const data of markerData) {
        const svgHtml = `
          <svg width="${size}" height="${size}" viewBox="0 0 24 24" style="transform: rotate(${data.angle + 180}deg); transform-origin: center;">
            <path d="M 12 2 L 12 20 L 6 14"
                  stroke="${data.color}"
                  stroke-width="${strokeWidth}"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  fill="none"/>
          </svg>
        `;

        // Ankerpunkt basierend auf Rotation - dreht sich mit
        // Im SVG ist der Anker am unteren Ende des langen Schenkels (12, 20)
        // Bei Rotation dreht sich dieser Punkt um die Mitte (12, 12)
        // +180° weil das SVG um 180° gedreht wird
        const normalizedAngle = (((data.angle + 180) % 360) + 360) % 360;
        const radians = (normalizedAngle * Math.PI) / 180;

        // Ursprünglicher Ankerpunkt relativ zur Mitte: (0, -8) -> oberes Ende des Schenkels (12, 4)
        // Nach Rotation um Mitte:
        const anchorOffsetX = 8 * Math.sin(radians);
        const anchorOffsetY = -8 * Math.cos(radians);

        // Anker in Icon-Koordinaten (Mitte ist size/2, size/2)
        const anchorX = (size / 2) + (anchorOffsetX * size / 24);
        const anchorY = (size / 2) + (anchorOffsetY * size / 24);

        const icon = L.divIcon({
          html: svgHtml,
          className: 'gpkg-haken-marker',
          iconSize: [size, size],
          iconAnchor: [anchorX, anchorY]
        });

        const marker = L.marker(data.latlng, {
          icon: icon,
          interactive: false
        });

        layerGroup.addLayer(marker);
      }
    };

    // Initial erstellen
    createMarkersForZoom(Math.round(this.map.getZoom()));

    // Bei Zoom-Änderung aktualisieren
    const zoomHandler = () => createMarkersForZoom(Math.round(this.map.getZoom()));
    this.map.on('zoomend', zoomHandler);

    // Handler für Cleanup speichern
    (layerGroup as any)._hakenZoomHandler = zoomHandler;

    return layerGroup;
  }

  /**
   * Sammelt Label-Daten für Canvas-Rendering.
   */
  collectGpkgLabelData(features: any[], gpkgStyle: 'standort' | 'humusform'): {center: L.LatLng, text: string, text2?: string, text3?: string}[] {
    const labelData: {center: L.LatLng, text: string, text2?: string, text3?: string}[] = [];

    for (const feature of features) {
      const labelInfo = this.getGpkgLabelText(feature, gpkgStyle);
      if (labelInfo) {
        const center = this.getPolygonVisualCenter(feature.geometry);
        if (center) {
          labelData.push({
            center,
            text: labelInfo.text,
            text2: labelInfo.text2,
            text3: labelInfo.text3
          });
        }
      }
    }

    return labelData;
  }

  /**
   * Extrahiert Label-Text aus einem Feature.
   * Bei Standort mit zwei Werten (LBFO2 vorhanden): 3 Zeilen mit Anteilen.
   */
  private getGpkgLabelText(feature: any, gpkgStyle: 'standort' | 'humusform'): {text: string, text2?: string, text3?: string} | null {
    const props = feature.properties || {};
    const getProp = (name: string) => props[name] || props[name.toLowerCase()] || props[name.toUpperCase()] || '';

    if (gpkgStyle === 'standort') {
      const lbfo1 = getProp('LBFO1');
      const subf1 = getProp('SUBF1');
      const stgr1 = getProp('STGR1');
      const sub1 = getProp('SUB1');

      if (!lbfo1 && !subf1) return null;

      // Zweiter Wert (falls vorhanden)
      const lbfo2 = getProp('LBFO2');
      const subf2 = getProp('SUBF2');
      const stgr2 = getProp('STGR2');
      const sub2 = getProp('SUB2');
      const hasSecond = !!(lbfo2 || subf2);

      // Zeile 1: LBFO1-SUBF1 (+ " - " LBFO2-SUBF2 falls vorhanden)
      let text = subf1 ? `${lbfo1}-${subf1}` : lbfo1;
      if (hasSecond) {
        const text1b = subf2 ? `${lbfo2}-${subf2}` : lbfo2;
        text = `${text} - ${text1b}`;
      }

      // Zeile 2: STGR1(SUB1) (+ " - " STGR2(SUB2) falls vorhanden)
      let text2 = sub1 ? `${stgr1}(${sub1})` : stgr1;
      if (hasSecond) {
        const text2b = sub2 ? `${stgr2}(${sub2})` : stgr2;
        if (text2b) text2 = `${text2} - ${text2b}`;
      }

      // Zeile 3: Anteile (nur wenn zweiter Wert vorhanden)
      let text3: string | undefined;
      if (hasSecond) {
        const ant1 = getProp('STO_ANT1');
        const ant2 = getProp('STO_ANT2');
        if (ant1 || ant2) {
          text3 = `${ant1 || '?'} - ${ant2 || '?'}`;
        }
      }

      return { text, text2: text2 || undefined, text3 };
    }

    if (gpkgStyle === 'humusform') {
      const hufo1 = getProp('HUFO1');
      if (!hufo1) return null;

      // Zweite Humusform (falls vorhanden)
      const hufo2 = getProp('HUFO2');
      const hasSecond = !!hufo2;

      // Zeile 1: HUFO1 (+ " - " HUFO2 falls vorhanden)
      let text = hufo1;
      if (hasSecond) {
        text = `${hufo1} - ${hufo2}`;
      }

      // Zeile 2: Anteile (nur wenn zweiter Wert vorhanden)
      let text2: string | undefined;
      if (hasSecond) {
        const ant1 = getProp('HU_ANT1');
        const ant2 = getProp('HU_ANT2');
        if (ant1 || ant2) {
          text2 = `${ant1 || '?'} - ${ant2 || '?'}`;
        }
      }

      return { text, text2 };
    }

    return null;
  }

  /**
   * Berechnet den optimalen Label-Punkt für ein Polygon.
   * Verwendet "Pole of Inaccessibility" - den Punkt mit maximalem Abstand zum Rand.
   * Ideal für nicht-konvexe Polygone (Hufeisen, Bögen, etc.)
   */
  getPolygonVisualCenter(geometry: any): L.LatLng | null {
    if (!geometry || !geometry.coordinates) return null;

    try {
      let allRings: number[][][];  // Array von Ringen (äußerer Ring + Löcher)
      if (geometry.type === 'Polygon') {
        allRings = geometry.coordinates;
      } else if (geometry.type === 'MultiPolygon') {
        allRings = geometry.coordinates[0];  // Erster Polygon des MultiPolygons
      } else {
        return null;
      }

      const outerRing = allRings[0];
      const holes = allRings.slice(1);  // Alle weiteren Ringe sind Löcher

      // Pole of Inaccessibility berechnen (mit Berücksichtigung von Löchern)
      const pole = this.findPoleOfInaccessibility(outerRing, holes);
      if (pole) {
        return new L.LatLng(pole[1], pole[0]);
      }

      // Fallback: Centroid des äußeren Rings
      let sumLat = 0, sumLng = 0;
      for (const coord of outerRing) {
        sumLng += coord[0];
        sumLat += coord[1];
      }
      return new L.LatLng(sumLat / outerRing.length, sumLng / outerRing.length);
    } catch {
      return null;
    }
  }

  /**
   * Findet den "Pole of Inaccessibility" - den Punkt im Polygon mit maximalem Abstand zum Rand.
   * Verwendet einen iterativen Grid-basierten Ansatz.
   * Berücksichtigt auch Löcher im Polygon.
   */
  private findPoleOfInaccessibility(outerRing: number[][], holes: number[][][] = [], precision = 0.0001): number[] | null {
    if (outerRing.length < 3) return null;

    // Bounding Box berechnen (nur äußerer Ring)
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const c of outerRing) {
      minX = Math.min(minX, c[0]);
      minY = Math.min(minY, c[1]);
      maxX = Math.max(maxX, c[0]);
      maxY = Math.max(maxY, c[1]);
    }

    const width = maxX - minX;
    const height = maxY - minY;
    const cellSize = Math.max(width, height) / 10;

    if (cellSize === 0) return [(minX + maxX) / 2, (minY + maxY) / 2];

    // Hilfsfunktion für Distanzberechnung mit Löchern
    const calcDist = (x: number, y: number): number => {
      return this.pointToPolygonDistWithHoles(x, y, outerRing, holes);
    };

    // Priority Queue als Array (sortiert nach Distanz)
    const cells: {x: number, y: number, h: number, d: number, max: number}[] = [];

    // Initiales Grid erstellen
    for (let x = minX; x < maxX; x += cellSize) {
      for (let y = minY; y < maxY; y += cellSize) {
        const h = cellSize / 2;
        const cx = x + h;
        const cy = y + h;
        const d = calcDist(cx, cy);
        cells.push({ x: cx, y: cy, h, d, max: d + h * Math.SQRT2 });
      }
    }

    // Centroid als Kandidat hinzufügen
    let sumX = 0, sumY = 0;
    for (const c of outerRing) {
      sumX += c[0];
      sumY += c[1];
    }
    const centroidX = sumX / outerRing.length;
    const centroidY = sumY / outerRing.length;
    const centroidDist = calcDist(centroidX, centroidY);

    let bestCell = { x: centroidX, y: centroidY, h: 0, d: centroidDist, max: centroidDist };

    // Beste Zelle finden
    for (const cell of cells) {
      if (cell.d > bestCell.d) bestCell = cell;
    }

    // Iterativ verfeinern
    let iterations = 0;
    const maxIterations = 100;

    while (cells.length > 0 && iterations < maxIterations) {
      iterations++;

      // Sortieren und beste Zelle nehmen
      cells.sort((a, b) => b.max - a.max);
      const cell = cells.shift()!;

      // Abbruch wenn keine Verbesserung mehr möglich
      if (cell.max - bestCell.d <= precision) break;

      // Zelle in 4 Teile aufteilen
      const h = cell.h / 2;
      for (const [dx, dy] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) {
        const cx = cell.x + dx * h;
        const cy = cell.y + dy * h;
        const d = calcDist(cx, cy);
        const newCell = { x: cx, y: cy, h, d, max: d + h * Math.SQRT2 };

        if (d > bestCell.d) bestCell = newCell;
        if (newCell.max > bestCell.d) cells.push(newCell);
      }
    }

    // Nur zurückgeben wenn Punkt innerhalb des Polygons liegt
    return bestCell.d >= 0 ? [bestCell.x, bestCell.y] : null;
  }

  /**
   * Berechnet die Distanz eines Punktes zum Polygon-Rand (mit Löchern).
   * Positiv = innerhalb des Polygons (außerhalb aller Löcher), negativ = außerhalb oder in einem Loch
   */
  private pointToPolygonDistWithHoles(x: number, y: number, outerRing: number[][], holes: number[][][] = []): number {
    // Prüfen ob Punkt im äußeren Ring liegt
    const inOuter = this.pointInPolygon(x, y, outerRing);
    if (!inOuter) {
      // Außerhalb des äußeren Rings - negative Distanz zum äußeren Rand
      return -this.distToRing(x, y, outerRing);
    }

    // Prüfen ob Punkt in einem Loch liegt
    for (const hole of holes) {
      if (this.pointInPolygon(x, y, hole)) {
        // Im Loch - negative Distanz zum Lochrand
        return -this.distToRing(x, y, hole);
      }
    }

    // Punkt ist im Polygon (außerhalb aller Löcher)
    // Distanz ist Minimum aus: Distanz zum äußeren Rand und Distanz zu allen Löchern
    let minDist = this.distToRing(x, y, outerRing);
    for (const hole of holes) {
      minDist = Math.min(minDist, this.distToRing(x, y, hole));
    }

    return minDist;
  }

  /**
   * Berechnet die kürzeste Distanz von einem Punkt zu einem Ring (Polygon-Rand)
   */
  private distToRing(x: number, y: number, ring: number[][]): number {
    let minDist = Infinity;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const dist = this.pointToSegmentDist(x, y, ring[j][0], ring[j][1], ring[i][0], ring[i][1]);
      minDist = Math.min(minDist, dist);
    }
    return minDist;
  }

  /**
   * Prüft ob ein Punkt innerhalb eines Polygons liegt (Ray-Casting)
   */
  private pointInPolygon(x: number, y: number, coords: number[][]): boolean {
    let inside = false;
    for (let i = 0, j = coords.length - 1; i < coords.length; j = i++) {
      const xi = coords[i][0], yi = coords[i][1];
      const xj = coords[j][0], yj = coords[j][1];
      if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
        inside = !inside;
      }
    }
    return inside;
  }

  /**
   * Berechnet die kürzeste Distanz von einem Punkt zu einem Liniensegment
   */
  private pointToSegmentDist(px: number, py: number, x1: number, y1: number, x2: number, y2: number): number {
    const dx = x2 - x1;
    const dy = y2 - y1;

    if (dx === 0 && dy === 0) {
      return Math.sqrt((px - x1) ** 2 + (py - y1) ** 2);
    }

    const t = Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / (dx * dx + dy * dy)));
    const nearestX = x1 + t * dx;
    const nearestY = y1 + t * dy;

    return Math.sqrt((px - nearestX) ** 2 + (py - nearestY) ** 2);
  }

  /**
   * Erstellt einen Canvas-basierten Label-Layer für optimale Performance.
   * Standort-Labels haben Priorität, Humus-Labels werden bei Kollision verschoben.
   */
  createCanvasLabelLayer(
    labelData: {center: L.LatLng, text: string, text2?: string, text3?: string}[],
    gpkgStyle: 'standort' | 'humusform'
  ): L.Layer {
    const isHumusform = gpkgStyle === 'humusform';
    const color = isHumusform ? '#228B22' : '#cc0000';
    const baseFontSize = isHumusform ? 12 : 8;  // Zeile 1 kleiner (war 10)
    const baseFontSize2 = 14;
    // Priorität: Standort = 1 (höher), Humus = 0 (niedriger)
    const priority = isHumusform ? 0 : 1;
    // Referenz auf die globale Label-Rechteck-Liste
    const globalLabelRects = this.gpkgLabelRects;
    // Referenz auf Punkt-Label-Rechtecke für Kollisionsprüfung
    const pointLabelRectsRef = this.pointLabelRects;
    // Getter für dynamische Punkt-Label-Berechnung (damit aktuelle bps verwendet werden)
    const getBps = () => this.bridge.getPoints();
    const markerHandlerRef = this.markerHandler;
    const minZoomForLabels = this.bridge.getPointLabelMinZoom();
    // Getter für Punkte-Layer-Sichtbarkeit
    const isPointsLayerVisible = () => this.bridge.isPointsLayerVisible();

    const CanvasLabelLayer = L.Layer.extend({
      initialize: function(labels: typeof labelData, options: any) {
        this._labels = labels;
        this._color = options.color;
        this._baseFontSize = options.baseFontSize;
        this._baseFontSize2 = options.baseFontSize2;
        this._isHumusform = options.isHumusform;
        this._priority = options.priority;
        this._globalLabelRects = options.globalLabelRects;
        this._pointLabelRects = options.pointLabelRects;
        this._getBps = options.getBps;
        this._markerHandler = options.markerHandler;
        this._minZoomForLabels = options.minZoomForLabels;
        this._isPointsLayerVisible = options.isPointsLayerVisible;
        L.Util.setOptions(this, options);
      },

      onAdd: function(map: L.Map) {
        this._map = map;
        this._canvas = L.DomUtil.create('canvas', 'gpkg-canvas-labels') as HTMLCanvasElement;
        this._canvas.style.position = 'absolute';
        this._canvas.style.pointerEvents = 'none';

        // Eigenes Pane für GPKG-Labels mit hohem Z-Index (über Markern)
        let pane = map.getPane('gpkg-labels');
        if (!pane) {
          map.createPane('gpkg-labels');
          pane = map.getPane('gpkg-labels');
          if (pane) pane.style.zIndex = '650';
        }
        if (pane) pane.appendChild(this._canvas);

        this._ctx = this._canvas.getContext('2d');

        // Throttled update - maximal alle 50ms
        this._lastUpdateTime = 0;
        this._pendingUpdate = false;

        const scheduleUpdate = () => {
          if (this._pendingUpdate) return;

          const now = Date.now();
          const timeSinceLastUpdate = now - this._lastUpdateTime;

          if (timeSinceLastUpdate >= 50) {
            // Sofort updaten
            this._lastUpdateTime = now;
            if (this._isHumusform) {
              setTimeout(() => this._update(), 30);
            } else {
              this._update();
            }
          } else {
            // Verzögert updaten
            this._pendingUpdate = true;
            setTimeout(() => {
              this._pendingUpdate = false;
              this._lastUpdateTime = Date.now();
              this._update();
            }, 50 - timeSinceLastUpdate);
          }
        };

        // moveend für vollständiges Re-Render
        map.on('moveend', scheduleUpdate, this);
        map.on('zoomend', scheduleUpdate, this);
        map.on('resize', this._resize, this);
        this._scheduleUpdateFn = scheduleUpdate;

        this._resize();
        scheduleUpdate();
      },

      onRemove: function(map: L.Map) {
        if (this._scheduleUpdateFn) {
          map.off('moveend', this._scheduleUpdateFn, this);
          map.off('zoomend', this._scheduleUpdateFn, this);
        }
        map.off('resize', this._resize, this);

        if (this._canvas && this._canvas.parentNode) {
          this._canvas.parentNode.removeChild(this._canvas);
        }

        // Gecachte Canvas-Referenzen aufräumen
        this._measureCanvas = null;
        this._measureCtx = null;
      },

      _resize: function() {
        const size = this._map.getSize();
        this._canvas.width = size.x;
        this._canvas.height = size.y;
      },

      // Berechnet Schriftgröße basierend auf Zoomlevel
      _getZoomFontSize: function(baseSize: number): number {
        const zoom = this._map.getZoom();
        // Zoom 15: kleiner, Zoom 16: normal, Zoom 17/18: größer
        if (zoom <= 15) {
          // Zoom 15 und darunter: 70% der Basisgröße (min 7px)
          return Math.max(7, Math.round(baseSize * 0.7));
        } else if (zoom === 16) {
          // Zoom 16: Basisgröße
          return baseSize;
        } else if (zoom === 17) {
          // Zoom 17: 130% der Basisgröße
          return Math.round(baseSize * 1.3);
        } else {
          // Zoom 18+: 160% der Basisgröße
          return Math.round(baseSize * 1.6);
        }
      },

      // Prüft Kollision mit existierenden GPKG-Rechtecken höherer oder gleicher Priorität
      _checkGpkgCollision: function(rect: {x: number, y: number, w: number, h: number}): boolean {
        const padding = 8; // Größerer Abstand zwischen GPKG-Labels
        for (const used of this._globalLabelRects) {
          // Nur kollidieren wenn andere Priorität >= eigene Priorität
          if (used.priority >= this._priority) {
            if (rect.x - padding < used.x + used.w &&
                rect.x + rect.w + padding > used.x &&
                rect.y - padding < used.y + used.h &&
                rect.y + rect.h + padding > used.y) {
              return true;
            }
          }
        }
        return false;
      },

      // Berechnet Punkt-Label-Rechtecke für aktuelle Karten-Position
      _computePointLabelRects: function(): {x: number, y: number, w: number, h: number}[] {
        const rects: {x: number, y: number, w: number, h: number}[] = [];
        // Keine Rechtecke wenn Punkte-Layer nicht sichtbar
        if (this._isPointsLayerVisible && !this._isPointsLayerVisible()) return rects;
        const bps = this._getBps ? this._getBps() : null;
        if (!this._map || !bps || !bps.length) return rects;

        const zoom = Math.round(this._map.getZoom());
        if (zoom < this._minZoomForLabels) return rects;

        const bounds = this._map.getBounds();
        const scale = Math.pow(2, zoom - 13);
        const maxFont = zoom >= 17 ? 14 : 12;
        const fontSize = Math.max(8, Math.min(maxFont, scale * 2.5));

        if (fontSize <= 8) return rects;

        // Gecachten Canvas für Textmessung verwenden
        if (!this._measureCanvas) {
          this._measureCanvas = document.createElement('canvas');
          this._measureCtx = this._measureCanvas.getContext('2d');
        }
        const ctx = this._measureCtx;
        if (!ctx) return rects;

        for (const marker of bps) {
          if (!marker.latLng) continue;
          if (!bounds.contains(marker.latLng)) continue;

          const containerPt = this._map.latLngToContainerPoint(marker.latLng);

          // Punkt-Symbol selbst (ca. 20x20px) als Kollisionsbereich
          const symbolSize = 20;
          rects.push({
            x: containerPt.x - symbolSize / 2,
            y: containerPt.y - symbolSize / 2,
            w: symbolSize,
            h: symbolSize
          });

          // Top-Label (Nummer über dem Punkt)
          if (marker.label) {
            ctx.font = `bold ${fontSize}px sans-serif`;
            const textWidth = ctx.measureText(marker.label).width;
            rects.push({
              x: containerPt.x - textWidth / 2,
              y: containerPt.y - 8 - fontSize,
              w: textWidth,
              h: fontSize
            });
          }

          // Side-Labels (rechts vom Punkt, ab Zoom 16)
          if (zoom >= 16 && marker.detailLabels) {
            const fsSmall = Math.max(8, fontSize - 2);
            ctx.font = `500 ${fsSmall}px sans-serif`;
            let yOffset = containerPt.y - 8;

            // Bodenform
            if (marker.detailLabels.bodenform) {
              const w = ctx.measureText(marker.detailLabels.bodenform).width;
              rects.push({ x: containerPt.x + 10, y: yOffset, w, h: fsSmall });
              yOffset += fsSmall + 2;
            }

            // Humusform
            if (marker.detailLabels.humusform) {
              const w = ctx.measureText(marker.detailLabels.humusform).width;
              rects.push({ x: containerPt.x + 10, y: yOffset, w, h: fsSmall });
            }
          }
        }

        return rects;
      },

      // Prüft Kollision mit Punkt-Labels (höchste Priorität)
      _checkPointLabelCollision: function(rect: {x: number, y: number, w: number, h: number}): boolean {
        const padding = 6; // Abstand zu Punkt-Labels
        // Nutze gecachte Punkt-Label-Rechtecke (berechnet am Anfang von _update)
        const pointRects = this._currentPointLabelRects || [];
        for (const used of pointRects) {
          if (rect.x - padding < used.x + used.w &&
              rect.x + rect.w + padding > used.x &&
              rect.y - padding < used.y + used.h &&
              rect.y + rect.h + padding > used.y) {
            return true;
          }
        }
        return false;
      },

      // Prüft Kollision mit allen relevanten Rechtecken
      _checkCollision: function(rect: {x: number, y: number, w: number, h: number}): boolean {
        return this._checkPointLabelCollision(rect) || this._checkGpkgCollision(rect);
      },

      // Findet eine kollisionsfreie Position für STO-Labels (gegen Punkt-Labels)
      // Gibt {dx, dy} zurück
      _findNonCollidingPosition: function(rect: {x: number, y: number, w: number, h: number}): {dx: number, dy: number} {
        const maxOffset = 60;
        const step = 6;

        // Zuerst nur vertikal versuchen
        for (let offset = step; offset <= maxOffset; offset += step) {
          const testRectDown = { ...rect, y: rect.y + offset };
          if (!this._checkPointLabelCollision(testRectDown)) {
            return { dx: 0, dy: offset };
          }
          const testRectUp = { ...rect, y: rect.y - offset };
          if (!this._checkPointLabelCollision(testRectUp)) {
            return { dx: 0, dy: -offset };
          }
        }

        // Dann nur horizontal versuchen
        for (let offset = step; offset <= maxOffset; offset += step) {
          const testRectRight = { ...rect, x: rect.x + offset };
          if (!this._checkPointLabelCollision(testRectRight)) {
            return { dx: offset, dy: 0 };
          }
          const testRectLeft = { ...rect, x: rect.x - offset };
          if (!this._checkPointLabelCollision(testRectLeft)) {
            return { dx: -offset, dy: 0 };
          }
        }

        // Dann diagonal versuchen
        for (let offset = step; offset <= maxOffset; offset += step) {
          const testRectRD = { ...rect, x: rect.x + offset, y: rect.y + offset };
          if (!this._checkPointLabelCollision(testRectRD)) {
            return { dx: offset, dy: offset };
          }
          const testRectLD = { ...rect, x: rect.x - offset, y: rect.y + offset };
          if (!this._checkPointLabelCollision(testRectLD)) {
            return { dx: -offset, dy: offset };
          }
          const testRectRU = { ...rect, x: rect.x + offset, y: rect.y - offset };
          if (!this._checkPointLabelCollision(testRectRU)) {
            return { dx: offset, dy: -offset };
          }
          const testRectLU = { ...rect, x: rect.x - offset, y: rect.y - offset };
          if (!this._checkPointLabelCollision(testRectLU)) {
            return { dx: -offset, dy: -offset };
          }
        }

        return { dx: 0, dy: 0 };
      },

      // Findet eine kollisionsfreie Position für Humus-Labels (gegen alle Kollisionen)
      // Gibt {dx, dy} zurück - die Verschiebung in beiden Richtungen
      _findNonCollidingPositionHumus: function(rect: {x: number, y: number, w: number, h: number}): {dx: number, dy: number} {
        const maxOffset = 80;
        const step = 6;

        // Zuerst nur vertikal versuchen
        for (let offset = step; offset <= maxOffset; offset += step) {
          const testRectDown = { ...rect, y: rect.y + offset };
          if (!this._checkCollision(testRectDown)) {
            return { dx: 0, dy: offset };
          }
          const testRectUp = { ...rect, y: rect.y - offset };
          if (!this._checkCollision(testRectUp)) {
            return { dx: 0, dy: -offset };
          }
        }

        // Dann nur horizontal versuchen
        for (let offset = step; offset <= maxOffset; offset += step) {
          const testRectRight = { ...rect, x: rect.x + offset };
          if (!this._checkCollision(testRectRight)) {
            return { dx: offset, dy: 0 };
          }
          const testRectLeft = { ...rect, x: rect.x - offset };
          if (!this._checkCollision(testRectLeft)) {
            return { dx: -offset, dy: 0 };
          }
        }

        // Dann diagonal versuchen (vertikal + horizontal)
        for (let offset = step; offset <= maxOffset; offset += step) {
          // Rechts-unten
          const testRectRD = { ...rect, x: rect.x + offset, y: rect.y + offset };
          if (!this._checkCollision(testRectRD)) {
            return { dx: offset, dy: offset };
          }
          // Links-unten
          const testRectLD = { ...rect, x: rect.x - offset, y: rect.y + offset };
          if (!this._checkCollision(testRectLD)) {
            return { dx: -offset, dy: offset };
          }
          // Rechts-oben
          const testRectRU = { ...rect, x: rect.x + offset, y: rect.y - offset };
          if (!this._checkCollision(testRectRU)) {
            return { dx: offset, dy: -offset };
          }
          // Links-oben
          const testRectLU = { ...rect, x: rect.x - offset, y: rect.y - offset };
          if (!this._checkCollision(testRectLU)) {
            return { dx: -offset, dy: -offset };
          }
        }

        return { dx: 0, dy: 0 };
      },

      _update: function() {
        if (!this._map || !this._ctx) return;

        const map = this._map;
        const ctx = this._ctx;
        const bounds = map.getBounds();
        const size = map.getSize();
        const topLeft = map.containerPointToLayerPoint([0, 0]);

        L.DomUtil.setPosition(this._canvas, topLeft);
        ctx.clearRect(0, 0, size.x, size.y);

        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        // Zoom-abhängige Schriftgrößen
        const fontSize = this._getZoomFontSize(this._baseFontSize);
        const fontSize2 = this._getZoomFontSize(this._baseFontSize2);
        const lineSpacing = Math.max(6, fontSize * 0.7);

        // Eigene Rechtecke (gleiche Priorität) aus der globalen Liste entfernen
        // So bleiben Rechtecke anderer Layer erhalten
        for (let i = this._globalLabelRects.length - 1; i >= 0; i--) {
          if (this._globalLabelRects[i].priority === this._priority) {
            this._globalLabelRects.splice(i, 1);
          }
        }

        // Punkt-Label-Rechtecke einmal pro Update berechnen (für Kollisionsprüfung)
        this._currentPointLabelRects = this._computePointLabelRects();

        for (const label of this._labels) {
          if (!bounds.contains(label.center)) continue;

          const point = map.latLngToContainerPoint(label.center);

          if (this._isHumusform) {
            // Humusform: ein- oder zweizeilig (mit Anteilen)
            const hasText2 = !!label.text2;
            const separator = ' - ';
            const parts1 = hasText2 ? label.text.split(separator) : [label.text];
            const parts2 = hasText2 && label.text2 ? label.text2.split(separator) : [];

            ctx.font = `400 ${fontSize}px sans-serif`;
            const separatorWidth = ctx.measureText(separator).width;

            let maxWidth: number;
            let leftWidth = 0, rightWidth = 0;

            if (hasText2 && parts1.length === 2) {
              // 2 Zeilen mit Separator: Ausrichtung berechnen
              const left1Width = ctx.measureText(parts1[0]).width;
              const right1Width = ctx.measureText(parts1[1]).width;
              const left2Width = parts2.length === 2 ? ctx.measureText(parts2[0]).width : 0;
              const right2Width = parts2.length === 2 ? ctx.measureText(parts2[1]).width : 0;

              leftWidth = Math.max(left1Width, left2Width);
              rightWidth = Math.max(right1Width, right2Width);
              maxWidth = leftWidth + separatorWidth + rightWidth;
            } else {
              // Einzeilig oder ohne Separator
              const text1Width = ctx.measureText(label.text).width;
              const text2Width = label.text2 ? ctx.measureText(label.text2).width : 0;
              maxWidth = Math.max(text1Width, text2Width);
            }

            // Höhe berechnen (engerer Zeilenabstand für Humus)
            const humusLineGap = 2; // Nur 2px zwischen den Zeilen
            let totalHeight = fontSize;
            if (hasText2) totalHeight += fontSize + humusLineGap;

            // Rechteck für Kollisionsprüfung
            let labelCenterX = point.x;
            let labelCenterY = point.y;
            const baseRect = {
              x: point.x - maxWidth / 2,
              y: point.y - totalHeight / 2,
              w: maxWidth,
              h: totalHeight
            };

            // Bei Kollision: verschieben (Humus gegen alle Kollisionen)
            if (this._checkCollision(baseRect)) {
              const offset = this._findNonCollidingPositionHumus(baseRect);
              labelCenterX = point.x + offset.dx;
              labelCenterY = point.y + offset.dy;
            }

            // Y-Positionen berechnen (enger zusammen)
            let y1: number, y2: number;
            if (hasText2) {
              y1 = labelCenterY - (fontSize + humusLineGap) / 2;
              y2 = labelCenterY + (fontSize + humusLineGap) / 2;
            } else {
              y1 = labelCenterY;
              y2 = 0;
            }

            // Rechteck registrieren
            this._globalLabelRects.push({
              x: labelCenterX - maxWidth / 2,
              y: labelCenterY - totalHeight / 2,
              w: maxWidth,
              h: totalHeight,
              priority: this._priority
            });

            // Dünner weißer Outline für Lesbarkeit
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
            ctx.lineWidth = 2;
            ctx.lineJoin = 'round';

            if (hasText2 && parts1.length === 2) {
              // 2 Zeilen mit ausgerichtetem Separator
              const separatorX = labelCenterX - maxWidth / 2 + leftWidth + separatorWidth / 2;

              // Zeile 1
              ctx.textAlign = 'right';
              ctx.strokeText(parts1[0], separatorX - separatorWidth / 2, y1);
              ctx.fillStyle = this._color;
              ctx.fillText(parts1[0], separatorX - separatorWidth / 2, y1);
              ctx.textAlign = 'center';
              ctx.strokeText(separator.trim(), separatorX, y1);
              ctx.fillText(separator.trim(), separatorX, y1);
              ctx.textAlign = 'left';
              ctx.strokeText(parts1[1], separatorX + separatorWidth / 2, y1);
              ctx.fillText(parts1[1], separatorX + separatorWidth / 2, y1);

              // Zeile 2
              if (parts2.length === 2) {
                ctx.textAlign = 'right';
                ctx.strokeText(parts2[0], separatorX - separatorWidth / 2, y2);
                ctx.fillText(parts2[0], separatorX - separatorWidth / 2, y2);
                ctx.textAlign = 'center';
                ctx.strokeText(separator.trim(), separatorX, y2);
                ctx.fillText(separator.trim(), separatorX, y2);
                ctx.textAlign = 'left';
                ctx.strokeText(parts2[1], separatorX + separatorWidth / 2, y2);
                ctx.fillText(parts2[1], separatorX + separatorWidth / 2, y2);
              }

              ctx.textAlign = 'center'; // Reset
            } else {
              // Einzeilig oder ohne Separator: zentriert
              ctx.strokeText(label.text, labelCenterX, y1);
              ctx.fillStyle = this._color;
              ctx.fillText(label.text, labelCenterX, y1);

              if (label.text2) {
                ctx.strokeText(label.text2, labelCenterX, y2);
                ctx.fillText(label.text2, labelCenterX, y2);
              }
            }
          } else {
            // Standort-Label (zwei- oder dreizeilig): leichtere Schrift
            const hasText3 = !!label.text3;

            // Bei 3 Zeilen: Text aufteilen um "-" auszurichten
            const separator = ' - ';
            const parts1 = hasText3 ? label.text.split(separator) : [label.text];
            const parts2 = hasText3 && label.text2 ? label.text2.split(separator) : [label.text2 || ''];
            const parts3 = hasText3 && label.text3 ? label.text3.split(separator) : [];

            // Breiten berechnen
            ctx.font = `400 ${fontSize}px sans-serif`;
            const separatorWidth = ctx.measureText(separator).width;

            let maxWidth: number;
            let leftWidth = 0, rightWidth = 0;

            if (hasText3 && parts1.length === 2) {
              // 3 Zeilen mit Separator: Ausrichtung berechnen
              ctx.font = `400 ${fontSize}px sans-serif`;
              const left1Width = ctx.measureText(parts1[0]).width;
              const right1Width = ctx.measureText(parts1[1]).width;
              ctx.font = `500 ${fontSize2}px sans-serif`;
              const left2Width = parts2.length === 2 ? ctx.measureText(parts2[0]).width : 0;
              const right2Width = parts2.length === 2 ? ctx.measureText(parts2[1]).width : 0;
              ctx.font = `400 ${fontSize}px sans-serif`;
              const left3Width = parts3.length === 2 ? ctx.measureText(parts3[0]).width : 0;
              const right3Width = parts3.length === 2 ? ctx.measureText(parts3[1]).width : 0;

              leftWidth = Math.max(left1Width, left2Width, left3Width);
              rightWidth = Math.max(right1Width, right2Width, right3Width);
              maxWidth = leftWidth + separatorWidth + rightWidth;
            } else {
              // 2 Zeilen oder kein Separator: wie bisher zentriert
              ctx.font = `400 ${fontSize}px sans-serif`;
              const text1Width = ctx.measureText(label.text).width;
              ctx.font = `500 ${fontSize2}px sans-serif`;
              const text2Width = label.text2 ? ctx.measureText(label.text2).width : 0;
              maxWidth = Math.max(text1Width, text2Width);
            }

            // Höhe berechnen je nach Anzahl der Zeilen
            let totalHeight = fontSize;
            if (label.text2) totalHeight += fontSize2 + lineSpacing;
            if (hasText3) totalHeight += fontSize + lineSpacing;

            // Basis-Rechteck für Kollisionsprüfung
            let labelCenterX = point.x;
            let labelCenterY = point.y;
            const baseRect = {
              x: point.x - maxWidth / 2,
              y: point.y - totalHeight / 2,
              w: maxWidth,
              h: totalHeight
            };

            // Bei Kollision mit Punkt-Labels: verschieben (2D)
            if (this._checkPointLabelCollision(baseRect)) {
              const offset = this._findNonCollidingPosition(baseRect);
              labelCenterX = point.x + offset.dx;
              labelCenterY = point.y + offset.dy;
            }

            // Y-Positionen berechnen (relativ zum ggf. verschobenen Zentrum)
            let y1: number, y2: number, y3: number;
            if (hasText3) {
              // 3 Zeilen: zentriert um labelCenterY
              y1 = labelCenterY - lineSpacing - fontSize2 / 2;
              y2 = labelCenterY;
              y3 = labelCenterY + lineSpacing + fontSize2 / 2;
            } else {
              // 2 Zeilen: wie bisher
              y1 = labelCenterY - lineSpacing;
              y2 = labelCenterY + lineSpacing;
              y3 = 0; // nicht verwendet
            }

            // Rechteck registrieren (Standort hat höhere Priorität)
            this._globalLabelRects.push({
              x: labelCenterX - maxWidth / 2,
              y: labelCenterY - totalHeight / 2,
              w: maxWidth,
              h: totalHeight,
              priority: this._priority
            });

            // Dünner weißer Outline für Lesbarkeit
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
            ctx.lineWidth = 2;
            ctx.lineJoin = 'round';

            if (hasText3 && parts1.length === 2) {
              // 3 Zeilen mit ausgerichtetem Separator
              const separatorX = labelCenterX - maxWidth / 2 + leftWidth + separatorWidth / 2;

              // Zeile 1
              ctx.font = `400 ${fontSize}px sans-serif`;
              ctx.textAlign = 'right';
              ctx.strokeText(parts1[0], separatorX - separatorWidth / 2, y1);
              ctx.fillStyle = this._color;
              ctx.fillText(parts1[0], separatorX - separatorWidth / 2, y1);
              ctx.textAlign = 'center';
              ctx.strokeText(separator.trim(), separatorX, y1);
              ctx.fillText(separator.trim(), separatorX, y1);
              ctx.textAlign = 'left';
              ctx.strokeText(parts1[1], separatorX + separatorWidth / 2, y1);
              ctx.fillText(parts1[1], separatorX + separatorWidth / 2, y1);

              // Zeile 2
              if (parts2.length === 2) {
                ctx.font = `500 ${fontSize2}px sans-serif`;
                ctx.textAlign = 'right';
                ctx.strokeText(parts2[0], separatorX - separatorWidth / 2, y2);
                ctx.fillText(parts2[0], separatorX - separatorWidth / 2, y2);
                ctx.textAlign = 'center';
                ctx.strokeText(separator.trim(), separatorX, y2);
                ctx.fillText(separator.trim(), separatorX, y2);
                ctx.textAlign = 'left';
                ctx.strokeText(parts2[1], separatorX + separatorWidth / 2, y2);
                ctx.fillText(parts2[1], separatorX + separatorWidth / 2, y2);
              }

              // Zeile 3
              if (parts3.length === 2) {
                ctx.font = `400 ${fontSize}px sans-serif`;
                ctx.textAlign = 'right';
                ctx.strokeText(parts3[0], separatorX - separatorWidth / 2, y3);
                ctx.fillStyle = this._color;
                ctx.fillText(parts3[0], separatorX - separatorWidth / 2, y3);
                ctx.textAlign = 'center';
                ctx.strokeText(separator.trim(), separatorX, y3);
                ctx.fillText(separator.trim(), separatorX, y3);
                ctx.textAlign = 'left';
                ctx.strokeText(parts3[1], separatorX + separatorWidth / 2, y3);
                ctx.fillText(parts3[1], separatorX + separatorWidth / 2, y3);
              }

              ctx.textAlign = 'center'; // Reset
            } else {
              // 2 Zeilen oder ohne Separator: wie bisher zentriert
              ctx.font = `400 ${fontSize}px sans-serif`;
              ctx.strokeText(label.text, labelCenterX, y1);
              ctx.fillStyle = this._color;
              ctx.fillText(label.text, labelCenterX, y1);

              if (label.text2) {
                ctx.font = `500 ${fontSize2}px sans-serif`;
                ctx.strokeText(label.text2, labelCenterX, y2);
                ctx.fillText(label.text2, labelCenterX, y2);
              }
            }
          }
        }
      }
    });

    return new (CanvasLabelLayer as any)(labelData, {
      color,
      baseFontSize,
      baseFontSize2,
      isHumusform,
      priority,
      globalLabelRects,
      pointLabelRects: pointLabelRectsRef,
      getBps,
      markerHandler: markerHandlerRef,
      minZoomForLabels,
      isPointsLayerVisible
    });
  }

  /**
   * Richtet den Zoom-Handler für GPKG-Labels ein.
   */
  setupGpkgZoomHandler(fullLayerName: string, mainLayer: L.Layer, labelLayer: L.Layer): void {
    // Entferne vorherigen Handler falls vorhanden
    const oldHandler = this.gpkgZoomHandlers.get(fullLayerName);
    if (oldHandler) {
      this.map.off('zoomend', oldHandler);
    }

    // Prüft ob der Layer via CSS versteckt ist (display: none)
    const isLayerCssHidden = (): boolean => {
      const ml = mainLayer as any;
      if (ml._container?.style?.display === 'none') return true;
      if (ml._path?.style?.display === 'none') return true;
      if (ml._layers) {
        const firstChild = Object.values(ml._layers)[0] as any;
        if (firstChild?._path?.style?.display === 'none') return true;
      }
      return false;
    };

    const updateVisibility = () => {
      const zoom = this.map.getZoom();
      const mainOnMap = this.map.hasLayer(mainLayer);
      const cssHidden = isLayerCssHidden();
      const manuallyHidden = this.hiddenGpkgLayers.has(fullLayerName);

      if (zoom >= this.GPKG_LABEL_MIN_ZOOM && mainOnMap && !cssHidden && !manuallyHidden) {
        if (!this.map.hasLayer(labelLayer)) {
          labelLayer.addTo(this.map);
        }
      } else {
        if (this.map.hasLayer(labelLayer)) {
          labelLayer.removeFrom(this.map);
        }
      }
    };

    this.gpkgZoomHandlers.set(fullLayerName, updateVisibility);
    this.map.on('zoomend', updateVisibility);

    // Initial visibility
    updateVisibility();
  }

  /**
   * Erzwingt ein Re-Render aller sichtbaren GPKG-Label-Layer.
   * Wird aufgerufen, wenn sich die Kollisionssituation ändert (z.B. Layer ein-/ausgeblendet).
   * Rendert STO-Layer (Priorität 1) zuerst, dann Humus-Layer (Priorität 0) mit Verzögerung.
   */
  refreshGpkgLabels(): void {
    // Globale Rechteck-Liste leeren, da sich die Situation geändert hat
    this.gpkgLabelRects.length = 0;

    const stoLayers: any[] = [];
    const humusLayers: any[] = [];

    // Layer nach Priorität sortieren
    for (const [, labelLayer] of this.gpkgLabelLayers) {
      if (this.map.hasLayer(labelLayer)) {
        const layer = labelLayer as any;
        if (layer._update) {
          if (layer._isHumusform) {
            humusLayers.push(layer);
          } else {
            stoLayers.push(layer);
          }
        }
      }
    }

    // STO zuerst rendern
    stoLayers.forEach(layer => layer._update());

    // Humus verzögert rendern (damit STO-Rechtecke vorhanden sind)
    if (humusLayers.length > 0) {
      setTimeout(() => {
        humusLayers.forEach(layer => layer._update());
      }, 50);
    }
  }

  /**
   * Öffnet das Style-Modal für einen GPKG-Layer (Style + Attribute, für Layer-Menü)
   */
  openGpkgStyleModal(layerFullName: string, geoPackageLayer: any, currentStyle: any): void {
    // Layer-Name aus fullLayerName extrahieren (Format: "Test.gpkg_sto")
    const match = layerFullName.match(/^(.+\.gpkg)_(.+)$/i);
    const displayName = match ? match[2] : layerFullName;

    // Modal öffnen
    const modalRef = this.modalService.open(DrawStyleModalComponent, {
      centered: true,
      size: 'lg'
    });

    // GPKG-Style-Modus aktivieren (mit Palette)
    modalRef.componentInstance.gpkgMode = true;
    modalRef.componentInstance.gpkgLayerName = displayName;
    modalRef.componentInstance.layerStyle = {
      strokeColor: currentStyle.color || '#3388ff',
      strokeWidth: currentStyle.weight || 2,
      fillColor: currentStyle.fillColor || 'transparent',
      fillOpacity: currentStyle.fillOpacity || 0
    };

    modalRef.result.then((newStyle) => {
      if (newStyle) {
        // Style auf Layer anwenden
        currentStyle.color = newStyle.strokeColor;
        currentStyle.weight = newStyle.strokeWidth;
        currentStyle.fillColor = newStyle.fillColor;
        currentStyle.fillOpacity = newStyle.fillOpacity;

        this.applyStyleToGeoPackageLayer(geoPackageLayer, currentStyle);

        // Style speichern
        const storageKey = `gpkgStyle_${layerFullName}`;
        this.storage.setSync(storageKey, currentStyle);
      }
    }).catch(() => {
      // Modal abgebrochen - nichts tun
    });
  }

  /**
   * Wendet einen Style auf alle Features eines GeoPackage-Layers an
   */
  applyStyleToGeoPackageLayer(geoPackageLayer: any, style: any): void {
    if (geoPackageLayer.eachLayer) {
      geoPackageLayer.eachLayer((layer: L.Layer) => {
        if ((layer as L.Path).setStyle) {
          (layer as L.Path).setStyle({
            color: style.color,
            weight: style.weight,
            fillColor: style.fillColor,
            fillOpacity: style.fillOpacity
          });
        }
      });
    }
  }

  /**
   * Steuert die Sichtbarkeit der GPKG-Label-Layer
   */
  updateLabelLayerVisibility(resolvedSublayerNames: string[]): void {
    // GPKG-Label-Layer Sichtbarkeit steuern (resolvedSublayerNames enthält qualifizierte Namen wie "datei.gpkg_sublayer")
    this.gpkgLabelLayers.forEach((labelLayer, fullLayerName) => {
      const shouldHide = resolvedSublayerNames.includes(fullLayerName);
      const ll = labelLayer as any;
      if (ll._canvas) {
        ll._canvas.style.display = shouldHide ? 'none' : '';
      }
      if (ll._container) {
        ll._container.style.display = shouldHide ? 'none' : '';
      }
    });

    // GPKG-Haken-Layer Sichtbarkeit steuern
    this.gpkgHakenLayers.forEach((hakenLayer, fullLayerName) => {
      const shouldHide = resolvedSublayerNames.includes(fullLayerName);
      if (shouldHide) {
        if (this.map.hasLayer(hakenLayer)) {
          this.map.removeLayer(hakenLayer);
        }
      } else {
        if (!this.map.hasLayer(hakenLayer)) {
          this.map.addLayer(hakenLayer);
        }
      }
    });

    // GPKG-Label-Sichtbarkeit sofort aktualisieren (für Zoom-Handler)
    this.gpkgZoomHandlers.forEach((handler) => handler());

    // GPKG-Labels neu rendern (Kollisionssituation hat sich geändert)
    setTimeout(() => this.refreshGpkgLabels(), 20);
  }

  /**
   * Zoom zu einem GPKG-Layer
   */
  zoomToGpkgLayer(fullLayerName: string, mapLayers: Record<string, L.Layer>): void {
    if (!this.map) return;

    const layer = mapLayers[fullLayerName] as any;
    if (!layer) return;

    if (typeof layer.getBounds === 'function') {
      const bounds = layer.getBounds();
      if (bounds && bounds.isValid()) {
        this.map.fitBounds(bounds, { padding: [20, 20] });
      }
    }
  }
}
